import { LngLat, type Map as MapLibreMap } from 'maplibre-gl';
import type { CameraAttributeValues } from './attribute-camera';
import { readGimbalCamera } from './gimbal-camera-adapter';
import { solveNightHawkGeometry } from './night-hawk-camera-geometry';
import { physicalCameraOptions } from './physical-camera-optics';
import { signedCameraOrientation } from './signed-camera-orientation';
import { CAMERA_TRACKS, CAMERA_TRACK_LIMITS } from '../domain/camera-keyframes';

const eventName = 'overland-traces:preview-camera';
export function requestPreviewCamera(document: Document, values: Partial<CameraAttributeValues> | undefined): boolean {
  const EventType = document.defaultView?.CustomEvent;
  if (!EventType) return false;
  const event = new EventType(eventName, { detail: { values }, cancelable: true });
  document.dispatchEvent(event); return event.defaultPrevented;
}

/** Use loaded terrain for drag feedback; exact terrain preparation remains in the renderer. */
export function observePreviewCamera(map: MapLibreMap, document: Document): () => void {
  let generation = 0;
  let baseline: ReturnType<typeof readGimbalCamera> | undefined;
  let values: CameraAttributeValues | undefined;
  let restore: (() => void) | undefined;
  const receive = (event: Event) => {
    const change = (event as CustomEvent<{ values?: Partial<CameraAttributeValues> }>).detail?.values;
    if (change && (!Object.keys(change).length || Object.entries(change).some(([axis, value]) => {
      const limit = CAMERA_TRACK_LIMITS[axis as keyof CameraAttributeValues];
      return !limit || typeof value !== 'number' || !Number.isFinite(value) || value < limit.min || value > limit.max;
    }))) return;
    event.preventDefault();
    const revision = ++generation;
    if (!change) { baseline = undefined; values = undefined; restore = undefined; return; }
    try {
      const data = map.getCanvas().dataset;
      baseline ??= readGimbalCamera(map);
      const numeric = (key: string, fallback: number) => Number.isFinite(Number(data[key])) ? Number(data[key]) : fallback;
      values = { ...(values ?? { rotation: numeric('cameraRotation', map.getBearing()), tilt: numeric('cameraRequestedTilt', map.getPitch()),
        roll: numeric('cameraBank', map.getRoll?.() ?? 0), altitude: numeric('cameraAltitude', Math.max(10, baseline.altitude - baseline.aim)),
        lens: numeric('cameraLens', 50) }), ...change };
      const pose = { ...values }, base = baseline;
      const elevation = async (point: readonly [number, number]) => map.queryTerrainElevation?.([...point]) ?? base.aim;
      void solveNightHawkGeometry(base.center, pose, elevation, undefined, base.aim).then(geometry => {
        if (revision !== generation) return;
        map.setMaxPitch(90); map.setCenterClampedToGround(false);
        const physical = { ground: geometry.ground, altitude: geometry.altitudeMsl, center: base.center, aim: geometry.aim };
        const options = { ...physicalCameraOptions(map, physical, pose.lens),
          ...signedCameraOrientation(pose.rotation, geometry.tilt, pose.roll), elevation: geometry.aim };
        const center = options.center ? LngLat.convert(options.center) : map.getCenter();
        const apply = () => {
          const at = map.getCenter();
          const angleDifference = (a: number, b: number) => Math.abs(((a - b + 540) % 360 + 360) % 360 - 180);
          if (angleDifference(map.getBearing(), options.bearing) > .001 || Math.abs(map.getPitch() - options.pitch) > .001
            || angleDifference(map.getRoll?.() ?? 0, options.roll) > .001 || Math.abs(map.getZoom() - (options.zoom ?? map.getZoom())) > .00001
            || Math.abs(at.lng - center.lng) > .000001 || Math.abs(at.lat - center.lat) > .000001) {
            physicalCameraOptions(map, physical, pose.lens);
            map.jumpTo(options);
          }
          for (const axis of CAMERA_TRACKS) data[axis === 'roll' ? 'cameraBank' : axis === 'tilt' ? 'cameraRequestedTilt' : `camera${axis[0]!.toUpperCase()}${axis.slice(1)}`] = String(pose[axis]);
        };
        restore = apply; apply();
      }).catch(() => { if (revision === generation) restore = undefined; });
    } catch { restore = undefined; }
  };
  const paint = () => restore?.();
  document.addEventListener(eventName, receive); map.on('render', paint);
  return () => { generation++; restore = undefined; document.removeEventListener(eventName, receive); map.off('render', paint); };
}
